/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { FunctionComponent } from 'preact';
import { HTMLAttributes } from 'preact/compat';
import { classes } from '@adobe-commerce/elsie/lib';
import { useText } from '@adobe-commerce/elsie/i18n';
import { formattedDate } from '@/quote-management/utils/dateUtils';
import '@/quote-management/components/QuoteHistoryLog/QuoteHistoryLog.css';

export interface HistoryEntry {
  uid: string;
  createdAt: string;
  author: {
    firstname: string;
    lastname: string;
  };
  changeType: string;
  changes: {
    commentAdded?: { comment: string };
    statuses?: { changes: Array<{ oldStatus?: string; newStatus?: string }> };
    expiration?: { oldExpiration?: string; newExpiration?: string };
    total?: {
      oldPrice?: { value: number; currency: string };
      newPrice?: { value: number; currency: string };
    };
    customChanges?: { title: string; old_value: string; new_value: string };
    productsRemoved?: {
      productsRemovedFromCatalog?: string[];
      productsRemovedFromQuote?: Array<{ name?: string; sku?: string; uid: string }>;
    };
  };
}

export interface ItemNote {
  createdAt: string;
  creatorId: number;
  creatorType: number;
  negotiableQuoteItemUid: string;
  note: string;
  noteUid: string;
}

export interface QuoteItem {
  product: {
    name: string;
    sku: string;
  };
  noteFromBuyer?: ItemNote[];
  noteFromSeller?: ItemNote[];
}

export interface QuoteHistoryLogProps extends HTMLAttributes<HTMLDivElement> {
  history?: HistoryEntry[];
  items?: QuoteItem[];
  buyer: {
    firstname: string;
    lastname: string;
  };
  salesRepName: string;
}

export const QuoteHistoryLog: FunctionComponent<QuoteHistoryLogProps> = ({
  className,
  children,
  history,
  items,
  buyer,
  salesRepName,
  ...props
}) => {
  const dictionary = useText({
    quoteCreated: 'historyLog.changeTypes.created',
    quoteUpdated: 'historyLog.changeTypes.updated',
    statusChanged: 'historyLog.changeTypes.statusChanged',
    commentAdded: 'historyLog.changeTypes.commentAdded',
    expirationChanged: 'historyLog.changeTypes.expirationChanged',
    buyerNoteAdded: 'historyLog.noteTypes.buyerNoteAdded',
    sellerNoteAdded: 'historyLog.noteTypes.sellerNoteAdded',
    buyerLabel: 'historyLog.authorLabels.buyer',
    sellerLabel: 'historyLog.authorLabels.seller',
    commentDetail: 'historyLog.changeDetails.comment',
    statusChangedFromTo: 'historyLog.changeDetails.statusChangedFromTo',
    statusSetTo: 'historyLog.changeDetails.statusSetTo',
    expirationChangedFromTo: 'historyLog.changeDetails.expirationChangedFromTo',
    expirationSetTo: 'historyLog.changeDetails.expirationSetTo',
    totalChangedFromTo: 'historyLog.changeDetails.totalChangedFromTo',
    customChange: 'historyLog.changeDetails.customChange',
    productsRemovedFromCatalog: 'historyLog.changeDetails.productsRemovedFromCatalog',
    productsRemovedFromQuote: 'historyLog.changeDetails.productsRemovedFromQuote',
    noDetailsAvailable: 'historyLog.changeDetails.noDetailsAvailable',
    emptyState: 'historyLog.emptyState',
    never: 'dateUtils.never',
  });


  const formatChangeType = (changeType: string) => {
    switch (changeType) {
      case 'CREATED':
        return dictionary.quoteCreated;
      case 'UPDATED':
        return dictionary.quoteUpdated;
      case 'STATUS_CHANGED':
        return dictionary.statusChanged;
      case 'COMMENT_ADDED':
        return dictionary.commentAdded;
      case 'EXPIRATION_CHANGED':
        return dictionary.expirationChanged;
      default:
        return changeType
          .replace(/_/g, ' ')
          .toLowerCase()
          .replace(/\b\w/g, (l) => l.toUpperCase());
    }
  };

  // Handle status or expiration changes
  const formatChanges = (changes: any) => {
    const changeDetails = [];

    if (changes?.commentAdded?.comment) {
      changeDetails.push(
        dictionary.commentDetail.replace(
          '{comment}',
          changes.commentAdded.comment
        )
      );
    }

    if (changes?.statuses?.changes) {
      changes.statuses.changes.forEach((statusChange: any) => {
        if (statusChange.oldStatus && statusChange.newStatus) {
          changeDetails.push(
            dictionary.statusChangedFromTo
              .replace('{oldStatus}', statusChange.oldStatus)
              .replace('{newStatus}', statusChange.newStatus)
          );
        } else if (statusChange.newStatus) {
          changeDetails.push(
            dictionary.statusSetTo.replace(
              '{newStatus}',
              statusChange.newStatus
            )
          );
        }
      });
    }

    if (
      changes?.expiration?.newExpiration &&
      changes?.expiration?.oldExpiration
    ) {
      const newDateFormatted = formattedDate(changes.expiration.newExpiration);
      const oldDateFormatted = formattedDate(changes.expiration.oldExpiration);
      changeDetails.push(
        dictionary.expirationChangedFromTo
          .replace('{oldExpiration}', oldDateFormatted)
          .replace('{newExpiration}', newDateFormatted)
      );
    } else if (changes?.expiration?.newExpiration) {
      const newDateFormatted = formattedDate(changes.expiration.newExpiration);
      changeDetails.push(
        dictionary.expirationSetTo.replace('{newExpiration}', newDateFormatted)
      );
    }

    if (changes?.total?.newPrice && changes?.total?.oldPrice) {
      const newTotal = `${changes.total.newPrice.currency} ${changes.total.newPrice.value}`;
      const oldTotal = `${changes.total.oldPrice.currency} ${changes.total.oldPrice.value}`;
      changeDetails.push(
        dictionary.totalChangedFromTo
          .replace('{oldTotal}', oldTotal)
          .replace('{newTotal}', newTotal)
      );
    }

    if (changes?.customChanges?.title && changes?.customChanges?.old_value && changes?.customChanges?.new_value) {
      changeDetails.push(
        dictionary.customChange
          .replace('{title}', changes.customChanges.title)
          .replace('{oldValue}', changes.customChanges.old_value)
          .replace('{newValue}', changes.customChanges.new_value)
      );
    }

    if (changes?.productsRemoved?.productsRemovedFromCatalog && changes.productsRemoved.productsRemovedFromCatalog.length > 0) {
      changeDetails.push(
        dictionary.productsRemovedFromCatalog.replace(
          '{products}',
          changes.productsRemoved.productsRemovedFromCatalog.join(', ')
        )
      );
    }

    if (changes?.productsRemoved?.productsRemovedFromQuote && changes.productsRemoved.productsRemovedFromQuote.length > 0) {
      const productNames = changes.productsRemoved.productsRemovedFromQuote.map((product: any) =>
        product.name || product.sku || product.uid
      ).join(', ');
      changeDetails.push(
        dictionary.productsRemovedFromQuote.replace('{products}', productNames)
      );
    }

    return changeDetails.length > 0
      ? changeDetails
      : [dictionary.noDetailsAvailable];
  };

  // Get item notes outside of history entries
  const getItemNotes = () => {
    const notes: Array<{
      createdAt: string;
      creatorId: number;
      creatorType: number;
      negotiableQuoteItemUid: string;
      note: string;
      noteUid: string;
      type: 'buyer' | 'seller';
      productName: string;
      productSku: string;
      authorName: string;
    }> = [];

    const processNotes = (
      noteArray: ItemNote[] | undefined,
      type: 'buyer' | 'seller',
      authorName: string,
      item: QuoteItem
    ) => {
      if (noteArray && Array.isArray(noteArray)) {
        noteArray.forEach((note) => {
          if (note && note.note && note.note.trim()) {
            notes.push({
              ...note,
              type,
              productName: item.product.name,
              productSku: item.product.sku,
              authorName,
            });
          }
        });
      }
    };

    if (items) {
      items.forEach((item) => {
        processNotes(
          item.noteFromBuyer,
          'buyer',
          `${buyer.firstname} ${buyer.lastname} ${dictionary.buyerLabel}`,
          item
        );

        processNotes(
          item.noteFromSeller,
          'seller',
          `${salesRepName} ${dictionary.sellerLabel}`,
          item
        );
      });
    }

    return notes;
  };

  const itemNotes = getItemNotes();

  // Get history entries
  const historyEntries = (history || []).map((entry) => ({
    ...entry,
    entryType: 'history' as const,
  }));
  const noteEntries = itemNotes.map((note) => ({
    ...note,
    entryType: 'note' as const,
  }));

  // Combine history and item notes without sorting
  const allEntries = [...historyEntries, ...noteEntries];

  return (
    <div
      {...props}
      className={classes(['quote-management-quote-history-log', className])}
    >
      {allEntries.length > 0 ? (
        <div className="quote-management-quote-history-log__entries">
          {allEntries.map((entry, index) => (
            <div
              key={
                entry.entryType === 'history'
                  ? entry.uid
                  : `${entry.noteUid}-${index}`
              }
              className="quote-management-quote-history-log__entry"
            >
              <div className="quote-management-quote-history-log__entry-header">
                <div className="quote-management-quote-history-log__entry-meta">
                  <span className="quote-management-quote-history-log__entry-date">
                    {formattedDate(entry.createdAt, 'long')}
                  </span>
                  <span className="quote-management-quote-history-log__entry-author">
                    {entry.entryType === 'history'
                      ? `by ${entry.author.firstname} ${entry.author.lastname
                      } ${entry.author.firstname === buyer.firstname &&
                        entry.author.lastname === buyer.lastname
                        ? dictionary.buyerLabel
                        : dictionary.sellerLabel
                      }`
                      : `by ${entry.authorName}`}
                  </span>
                </div>
                <div className="quote-management-quote-history-log__entry-type">
                  {entry.entryType === 'history'
                    ? formatChangeType(entry.changeType)
                    : entry.type === 'buyer'
                      ? dictionary.buyerNoteAdded
                      : dictionary.sellerNoteAdded}
                </div>
              </div>
              <div className="quote-management-quote-history-log__entry-changes">
                {entry.entryType === 'history' ? (
                  formatChanges(entry.changes).map((change, changeIndex) => (
                    <div
                      key={changeIndex}
                      className="quote-management-quote-history-log__entry-change"
                    >
                      {change}
                    </div>
                  ))
                ) : (
                  <div className="quote-management-quote-history-log__entry-change">
                    <div>
                      {entry.productName}({entry.productSku})
                    </div>
                    <div>"{entry.note}"</div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="quote-management-quote-history-log__empty">
          {dictionary.emptyState}
        </div>
      )}
      {children}
    </div>
  );
};
